import { Auth0Client, type RedirectLoginOptions } from '@auth0/auth0-spa-js'
import { env } from '@/infra/env'
import type {
  AuthInitialScreen,
  AuthProvider,
  ProviderLogoutOptions,
  SessionInfo,
} from '@/features/iam/auth/AuthStore'
import {
  decodeTokenClaims,
  isMfaEnrolled,
} from '@/features/iam/auth/token-claims'
import { verifyAuth0IdToken } from '@/features/iam/auth/verify-auth0-id-token'

const AUTH0_SCOPE = 'openid profile email offline_access'

/**
 * The parts of the Auth0 client the {@link Auth0Provider} relies on.
 */
export type Auth0ClientLike = Pick<
  Auth0Client,
  | 'getTokenSilently'
  | 'getUser'
  | 'handleRedirectCallback'
  | 'isAuthenticated'
  | 'loginWithRedirect'
  | 'logout'
>

/**
 * Provides authentication through Auth0.
 */
export class Auth0Provider implements AuthProvider {
  constructor(private readonly _auth0: Auth0ClientLike) {}

  async fetchSessionInfo(): Promise<SessionInfo | null> {
    if (
      !(await this._auth0.isAuthenticated()) &&
      !(await this._isVerifiedImpersonationLanding())
    ) {
      return null
    }

    // Client caches tokens in localstorage and only reaches out to
    // auth0 when token is about to expire
    const token = await this._auth0.getTokenSilently()
    const user = await this._auth0.getUser()
    if (!user?.sub) {
      throw new Error('No user returned from getUser.')
    }

    return {
      token,
      authId: user.sub,
      mfaEnabled: isMfaEnrolled(decodeTokenClaims(token)),
    }
  }

  async handleRedirectCallback() {
    await this._auth0.handleRedirectCallback()
  }

  redirectToAuthorize(
    initialScreen: AuthInitialScreen,
    opts: RedirectLoginOptions | undefined,
  ) {
    void this._auth0.logout({
      openUrl: false,
    })

    return this._auth0.loginWithRedirect({
      ...opts,
      appState: {
        customReferrerParams: {},
      },
      authorizationParams: {
        ...opts?.authorizationParams,
        initial_screen: initialScreen || 'login',
        screen_hint: initialScreen?.toLowerCase() || 'login',
        referrer: document.location.href,
        scope: AUTH0_SCOPE,
        connections: window.__auth0Connections.filter(
          (s) => s !== 'rocket-lawyer-sso',
        ),
      },
    })
  }

  logout({ returnTo, federated }: ProviderLogoutOptions) {
    return this._auth0.logout({
      logoutParams: { returnTo, federated },
    })
  }

  /**
   * Verified impersonation landings are exempt from the MFA-bypass guard.
   */
  private async _isVerifiedImpersonationLanding(): Promise<boolean> {
    const idToken = new URLSearchParams(
      window.location.hash.replace(/^#/, ''),
    ).get('id_token')
    if (!idToken) {
      return false
    }
    return verifyAuth0IdToken(idToken)
  }

  /**
   * Creates an {@link Auth0Provider} configured for this environment.
   */
  static create() {
    return new Auth0Provider(
      new Auth0Client({
        clientId: env.AUTH0_CLIENT_ID,
        useRefreshTokensFallback: true,
        authorizationParams: {
          audience: env.AUTH0_AUDIENCE_URL,
          redirect_uri: window.location.origin,
          scope: AUTH0_SCOPE,
        },
        domain: env.AUTH0_DOMAIN,
        useRefreshTokens: true,
        cacheLocation: 'localstorage',
      }),
    )
  }
}
